/**
 * The fab world provider (M8 MC-c5b): what the Outfitting tab's fab rows read of the live world besides the Career
 * (`FabOptions`: the docked system for Mk III geography, MC-c4's shortage world, MC-b7's remote berth pull). The
 * UI has no Reach, galaxy or strategic client, so a feature that has them (`features/fabWorld/feature.ts`) registers
 * a provider here and `models/view.ts` reads it when it builds the Outfitting offers. Without one (M2's own career,
 * the unit tests, before the feature's init) the offers get no options, as before. No `UiActions` member.
 *
 * The same provider gives the other station tabs what they read of the strategic economy (`StationWorld`, K12):
 * the Repair tab whether the strategic economy prices the docked station (`linked`, the runtime's `strategicAt`
 * rule), and the Contracts tab the local system view (`view`, a works site's live need).
 */
import type { WorksView } from '../../contracts/commands'
import type { M2Data } from '../../data/m2Schemas'
import type { FabOptions } from './outfitting'

/** The fab options plus the strategic signals the Repair and Contracts tabs read (absent: unknown, the old rows). */
export interface StationWorld extends FabOptions {
  /** Whether the strategic economy prices M2 station `stationId` (the career runtime's `strategicAt`: the market resolves it). */
  linked?: (stationId: string) => boolean
  /** The strategic client's current system view (works rows: what a site still misses), or null. */
  view?: WorksView | null
}

/** Builds the fab options for one view build; `now` is the snapshot's game time (berth access stamps). */
export type FabWorldProvider = (data: M2Data, now: number) => StationWorld

const NONE: StationWorld = Object.freeze({})

let provider: FabWorldProvider | null = null

/** Registers the provider (one at a time: a later one replaces it); the returned function removes it if still current. */
export function registerFabWorld(p: FabWorldProvider): () => void {
  provider = p
  return () => {
    if (provider === p) provider = null
  }
}

/** The fab options for this view build, or none when no provider is registered. */
export function fabWorldOptions(data: M2Data, now: number): StationWorld {
  return provider === null ? NONE : provider(data, now)
}
